import { useState, type ReactNode } from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Bell,
  BellOff,
  Circle,
  CircleCheck,
  ExternalLink,
  Link2,
  MessageCircle,
  MoreHorizontal,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import type { ThreadSummary } from '@rundown/types';
import { Modal } from '../../components';
import { useToast } from '../../components';
import { cx } from '../../lib/cx';
import { queryKeys } from '../../lib/queryKeys';
import { unwatchRequest, watchRequest } from '../notifications/api';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import {
  archiveThread,
  deleteThread,
  keepAsChat,
  markThreadRead,
  markThreadUnread,
  unarchiveThread,
} from './api';

/** One thing the menu can do. The same list feeds right-click and the `⋯` button. */
export interface MenuAction {
  readonly key: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly onSelect: () => void;
  readonly danger?: boolean;
  /** Draw a divider above this item. */
  readonly separated?: boolean;
}

const ITEM_CLASS =
  'flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 fs-12.5 text-ink outline-none select-none data-[highlighted]:bg-surface-3';
const CONTENT_CLASS =
  'z-50 min-w-[12rem] rounded-lg border border-line bg-base p-1 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.25)]';

function Items({ actions, as }: { actions: readonly MenuAction[]; as: 'context' | 'dropdown' }) {
  const Item = as === 'context' ? ContextMenu.Item : DropdownMenu.Item;
  const Separator = as === 'context' ? ContextMenu.Separator : DropdownMenu.Separator;
  return (
    <>
      {actions.map((action) => (
        <div key={action.key}>
          {action.separated === true && <Separator className="my-1 h-px bg-line" />}
          <Item
            onSelect={action.onSelect}
            className={cx(
              ITEM_CLASS,
              action.danger === true && 'text-danger data-[highlighted]:bg-danger-tint',
            )}
          >
            <action.icon size={13} strokeWidth={2} aria-hidden className="flex-none" />
            {action.label}
          </Item>
        </div>
      ))}
    </>
  );
}

/** Right-click anywhere on `children` for these actions. */
export function ActionContextMenu({
  actions,
  children,
}: {
  actions: readonly MenuAction[];
  children: ReactNode;
}) {
  if (actions.length === 0) return <>{children}</>;
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className={CONTENT_CLASS}>
          <Items actions={actions} as="context" />
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

/** The same actions behind a `⋯` button — for touch, and for anyone who never right-clicks. */
export function ActionMenuButton({
  actions,
  label = 'More actions',
  className,
}: {
  actions: readonly MenuAction[];
  label?: string;
  className?: string;
}) {
  if (actions.length === 0) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={label}
        title={label}
        onClick={(event) => event.stopPropagation()}
        className={cx('rounded-md p-1 text-muted hover:bg-surface-3 hover:text-ink', className)}
      >
        <MoreHorizontal size={14} strokeWidth={2} aria-hidden />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4} className={CONTENT_CLASS}>
          <Items actions={actions} as="dropdown" />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Everything a person can do to a thread from wherever it is listed: read
 * state, following, archive, and the author's and admin's heavier actions.
 * Returns the actions plus the delete confirmation, which has to be rendered
 * somewhere outside the menu (a menu closes the moment an item is chosen).
 */
export function useThreadActions(thread: ThreadSummary): { actions: MenuAction[]; dialogs: ReactNode } {
  const { workspace } = useRequiredWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { say, warn } = useToast();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspace.id) });
  };
  const run = useMutation({
    mutationFn: (work: () => Promise<unknown>) => work(),
    onSuccess: refresh,
    onError: (error: Error) => warn(error.message),
  });
  const remove = useMutation({
    mutationFn: () => deleteThread(workspace.id, thread.id),
    onSuccess: () => {
      setConfirmingDelete(false);
      queryClient.removeQueries({ queryKey: queryKeys.thread(workspace.id, thread.id) });
      refresh();
      void navigate('/');
    },
  });

  const actions: MenuAction[] = [
    thread.unread
      ? {
          key: 'read',
          label: 'Mark as read',
          icon: CircleCheck,
          onSelect: () => run.mutate(() => markThreadRead(workspace.id, thread.id)),
        }
      : {
          key: 'unread',
          label: 'Mark as unread',
          icon: Circle,
          onSelect: () => run.mutate(() => markThreadUnread(workspace.id, thread.id)),
        },
    thread.following
      ? {
          key: 'unfollow',
          label: 'Unfollow',
          icon: BellOff,
          onSelect: () => run.mutate(() => unwatchRequest(workspace.id, thread.id)),
        }
      : {
          key: 'follow',
          label: 'Follow',
          icon: Bell,
          onSelect: () => run.mutate(() => watchRequest(workspace.id, thread.id)),
        },
    thread.archived
      ? {
          key: 'unarchive',
          label: 'Move back to threads',
          icon: ArchiveRestore,
          onSelect: () => run.mutate(() => unarchiveThread(workspace.id, thread.id)),
        }
      : {
          key: 'archive',
          label: 'Archive for me',
          icon: Archive,
          onSelect: () =>
            run.mutate(async () => {
              await archiveThread(workspace.id, thread.id);
              say('Archived. It comes back if someone posts in it.');
            }),
        },
    {
      key: 'copy',
      label: 'Copy link',
      icon: Link2,
      separated: true,
      onSelect: () => {
        void navigator.clipboard
          ?.writeText(`${window.location.origin}/t/${thread.id}`)
          .then(() => say('Link copied.'));
      },
    },
  ];
  if (thread.kind === 'issue') {
    actions.push({
      key: 'full',
      label: 'Open as full page',
      icon: ExternalLink,
      onSelect: () => void navigate(`/requests/${thread.id}`),
    });
  }
  if (thread.canKeepAsChat) {
    actions.push({
      key: 'keep',
      label: 'Keep as chat',
      icon: MessageCircle,
      separated: true,
      onSelect: () => run.mutate(() => keepAsChat(workspace.id, thread.id)),
    });
  }
  if (thread.canDelete) {
    actions.push({
      key: 'delete',
      label: 'Delete thread',
      icon: Trash2,
      danger: true,
      separated: !thread.canKeepAsChat,
      onSelect: () => setConfirmingDelete(true),
    });
  }

  const dialogs = (
    <Modal
      open={confirmingDelete}
      onOpenChange={setConfirmingDelete}
      title="Delete this thread?"
      description="It is removed for everyone, with every message in it. This cannot be undone."
      footer={
        <div className="flex items-center justify-end gap-2">
          {remove.error !== null && <span className="mr-auto fs-12 text-danger">{remove.error.message}</span>}
          <button
            type="button"
            onClick={() => setConfirmingDelete(false)}
            className="rounded-control border border-line bg-base px-3 py-1.5 fs-12"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            className="rounded-control bg-danger px-3 py-1.5 fs-12 font-semibold text-white disabled:opacity-50"
          >
            {remove.isPending ? 'Deleting…' : 'Delete thread'}
          </button>
        </div>
      }
    >
      <p className="fs-13 text-ink-soft">{thread.title}</p>
    </Modal>
  );

  return { actions, dialogs };
}
